import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import {
  filterGdLohnartenForUser,
  getGdLohnarten,
  type GdLohnart,
} from "@/lib/dokumente";
import {
  saveOutstandingMaterialPricing,
  type OutstandingMaterialItem,
} from "@/lib/outstandingBalances";
import { DocumentPositionEditDialog } from "@/pages/dokumente/DocumentPositionEditDialog";
import {
  createBlockPosition,
  newLeistungArtikel,
  newLocalId,
  syncLeistungEinzelpreis,
  type EditorPosition,
  type LeistungKalkulation,
} from "@/pages/dokumente/documentPositionModel";

function buildEditorPositionFromMaterial(
  material: OutstandingMaterialItem,
  lohnarten: GdLohnart[],
): EditorPosition {
  const base = createBlockPosition("leistung", 0, { lohnarten });
  const saved = material.kalkulation_json;
  let kalk: LeistungKalkulation;

  if (saved && typeof saved === "object" && Array.isArray((saved as { artikel_liste?: unknown }).artikel_liste)) {
    const raw = saved as unknown as LeistungKalkulation;
    kalk = {
      artikel_liste: (raw.artikel_liste || []).map((row) => ({
        ...newLeistungArtikel({ einheit: row.einheit ?? material.unit }),
        ...row,
        localId: row.localId || newLocalId("art"),
      })),
      lohn_liste:
        Array.isArray(raw.lohn_liste) && raw.lohn_liste.length > 0
          ? raw.lohn_liste.map((row) => ({
              localId: row.localId || newLocalId("lohn"),
              lohnart_id: row.lohnart_id || "",
              bezeichnung: row.bezeichnung || "",
              stunden: Number(row.stunden) || 0,
              stundensatz_netto: Number(row.stundensatz_netto) || 0,
              preisbezug: row.preisbezug === "gesamt" ? "gesamt" : "einzel",
              interne_beschreibung: row.interne_beschreibung,
            }))
          : lohnarten.map((art) => ({
              localId: newLocalId("lohn"),
              lohnart_id: art.id,
              bezeichnung: art.bezeichnung,
              stunden: 0,
              stundensatz_netto: art.stundensatz_netto,
            })),
      stunden: Number(raw.stunden) || 0,
      stundensatz_netto: Number(raw.stundensatz_netto) || 0,
      pauschale_netto: Number(raw.pauschale_netto) || 0,
      aufschlag_prozent: Number(raw.aufschlag_prozent) || 0,
      vk_driver: raw.vk_driver,
    };
  } else {
    const artikel = newLeistungArtikel({ einheit: material.unit });
    artikel.bezeichnung = material.label || "Material";
    artikel.menge = Number(material.quantity) > 0 ? Number(material.quantity) : 1;
    artikel.hero_nr = material.hero_nr;
    artikel.artikel_id = material.artikel_id ?? null;
    artikel.ist_freier_artikel = material.artikel_id == null;
    if (material.unit_price != null && Number.isFinite(material.unit_price)) {
      artikel.einzelpreis_netto = Number(material.unit_price);
    }
    kalk = {
      ...(base.kalkulation_json as LeistungKalkulation),
      artikel_liste: [artikel],
    };
  }

  const draft: EditorPosition = {
    ...base,
    bezeichnung: material.label || "Material",
    menge: 1,
    einheit: "pauschal",
    hero_nr: material.hero_nr,
    artikel_id: material.artikel_id ?? null,
    einzelpreis_netto: material.unit_price ?? 0,
    kalkulation_json: kalk,
  };
  return syncLeistungEinzelpreis(draft);
}

export function OutstandingMaterialKalkDialog({
  material,
  open,
  onClose,
  onSaved,
}: {
  material: OutstandingMaterialItem | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useCurrentUser();
  const [lohnarten, setLohnarten] = useState<GdLohnart[]>([]);
  const [loadingLohnarten, setLoadingLohnarten] = useState(false);
  const [position, setPosition] = useState<EditorPosition | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visibleLohnarten = useMemo(
    () => filterGdLohnartenForUser(lohnarten, user),
    [lohnarten, user],
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingLohnarten(true);
    setError(null);
    void getGdLohnarten()
      .then((rows) => {
        if (!cancelled) setLohnarten(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : "Lohnarten konnten nicht geladen werden.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingLohnarten(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !material || loadingLohnarten) {
      setPosition(null);
      return;
    }
    setPosition(buildEditorPositionFromMaterial(material, visibleLohnarten));
  }, [open, material, loadingLohnarten, visibleLohnarten]);

  const handleSave = async (next: EditorPosition) => {
    if (!material?.id) return;
    const synced = syncLeistungEinzelpreis(next);
    setSaving(true);
    setError(null);
    try {
      await saveOutstandingMaterialPricing(material.timesheet_id, material.id, {
        einzelpreis_netto: Number(synced.einzelpreis_netto) || 0,
        kalkulation_json: (synced.kalkulation_json as Record<string, unknown>) || null,
        text: synced.bezeichnung || material.label,
        einheit: material.unit,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Materialkalkulation konnte nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  if (loadingLohnarten || !position) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30">
        <div className="rounded-ios bg-card px-6 py-5 shadow-ios-md">
          {error ? (
            <Alert variant="error" title="Fehler">
              {error}
            </Alert>
          ) : (
            <div className="flex items-center gap-2 text-sm text-label-secondary">
              <Spinner />
              Kalkulation wird vorbereitet …
            </div>
          )}
          {error ? (
            <button
              type="button"
              className="mt-3 text-sm text-tint"
              onClick={onClose}
            >
              Schließen
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <>
      {error ? (
        <div className="fixed bottom-4 right-4 z-[90] max-w-sm">
          <Alert variant="error" title="Speichern fehlgeschlagen">
            {error}
          </Alert>
        </div>
      ) : null}
      {saving ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/20">
          <div className="flex items-center gap-2 rounded-ios bg-card px-4 py-3 text-sm shadow-ios-md">
            <Spinner />
            Wird gespeichert …
          </div>
        </div>
      ) : null}
      <DocumentPositionEditDialog
        open={open && !saving}
        position={position}
        editable
        currency="EUR"
        lohnarten={visibleLohnarten}
        auftragId={material?.auftrag_id ?? null}
        dokumentTyp={null}
        onClose={onClose}
        onSave={(pos) => void handleSave(pos)}
        zIndexClass="z-[80]"
      />
    </>
  );
}
